import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import toast from "../../utils/toast";
import { useTranslation } from "react-i18next";
import { apiService } from "../../services/apiService";
import { ActivityDefinition, User, UserRole } from "../../types";
import { useAuth } from "../../contexts/AuthContext";

export type AssignableProject = {
  id: number;
  projectName: string;
  workflowName?: string | null;
  projectCode?: string | null;
  templateWorkflowId?: number | null;
};

interface AssignTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  projects: AssignableProject[];
  users: User[];
  initialWorkflowInstanceId?: number | null;
}

type SelectOption = {
  value: number;
  label: string;
  subtitle?: string;
};

const normalizeSearchText = (value?: string | null): string =>
  String(value || "")
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\u0131/g, "i")
    .trim();

const parseNodesSnapshotActivityIds = (nodesSnapshot?: string | null): number[] => {
  if (!nodesSnapshot) return [];
  try {
    const parsed = JSON.parse(nodesSnapshot);
    if (!Array.isArray(parsed)) return [];
    const ids = parsed
      .map((node: any) => Number(node?.activityId))
      .filter((id: number) => Number.isFinite(id));
    return Array.from(new Set(ids));
  } catch (error) {
    return [];
  }
};

const SearchableSingleSelect: React.FC<{
  value: number | null;
  onChange: (value: number | null) => void;
  options: SelectOption[];
  placeholder: string;
  disabled?: boolean;
  searchPlaceholder: string;
  emptyText: string;
}> = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  searchPlaceholder,
  emptyText,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (event: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    const normalized = normalizeSearchText(query);
    if (!normalized) return options;
    return options.filter((opt) =>
      normalizeSearchText(`${opt.label} ${opt.subtitle || ""}`).includes(
        normalized,
      ),
    );
  }, [options, query]);

  const selected = options.find((opt) => opt.value === value) || null;

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        disabled={disabled}
        className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm flex items-center justify-between bg-white disabled:bg-slate-100 disabled:text-slate-400"
      >
        <span className="truncate text-left">
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDownIcon className="w-4 h-4 text-slate-500 ml-2 shrink-0" />
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-[60] mt-2 w-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setIsOpen(false);
              }}
              className="w-full px-3 py-2.5 text-left text-sm hover:bg-slate-50"
            >
              {placeholder}
            </button>
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-3 text-sm text-slate-500">{emptyText}</div>
            ) : (
              filteredOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`w-full px-3 py-2.5 text-left text-sm hover:bg-slate-50 ${
                    value === opt.value ? "bg-indigo-50 text-indigo-700" : ""
                  }`}
                >
                  <div className="font-medium">{opt.label}</div>
                  {opt.subtitle ? (
                    <div className="text-xs text-slate-500 mt-0.5 truncate">
                      {opt.subtitle}
                    </div>
                  ) : null}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SearchableMultiSelect: React.FC<{
  values: number[];
  onChange: (values: number[]) => void;
  options: SelectOption[];
  placeholder: string;
  disabled?: boolean;
  searchPlaceholder: string;
  emptyText: string;
}> = ({
  values,
  onChange,
  options,
  placeholder,
  disabled = false,
  searchPlaceholder,
  emptyText,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (event: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    const normalized = normalizeSearchText(query);
    if (!normalized) return options;
    return options.filter((opt) =>
      normalizeSearchText(`${opt.label} ${opt.subtitle || ""}`).includes(
        normalized,
      ),
    );
  }, [options, query]);

  const selectedLabel = useMemo(() => {
    if (values.length === 0) return placeholder;
    const selectedOptions = options.filter((opt) => values.includes(opt.value));
    if (selectedOptions.length <= 2) {
      return selectedOptions.map((opt) => opt.label).join(", ");
    }
    return `${selectedOptions.length} kişi seçildi`;
  }, [options, placeholder, values]);

  const toggleValue = (target: number) => {
    if (values.includes(target)) {
      onChange(values.filter((id) => id !== target));
      return;
    }
    onChange([...values, target]);
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        disabled={disabled}
        className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm flex items-center justify-between bg-white disabled:bg-slate-100 disabled:text-slate-400"
      >
        <span className="truncate text-left">{selectedLabel}</span>
        <ChevronDownIcon className="w-4 h-4 text-slate-500 ml-2 shrink-0" />
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-[60] mt-2 w-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-3 text-sm text-slate-500">{emptyText}</div>
            ) : (
              filteredOptions.map((opt) => (
                <label
                  key={opt.value}
                  className="w-full px-3 py-2.5 flex items-start gap-2 text-sm hover:bg-slate-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={values.includes(opt.value)}
                    onChange={() => toggleValue(opt.value)}
                    className="mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="min-w-0">
                    <span className="block font-medium truncate">{opt.label}</span>
                    {opt.subtitle ? (
                      <span className="block text-xs text-slate-500 truncate">
                        {opt.subtitle}
                      </span>
                    ) : null}
                  </span>
                </label>
              ))
            )}
          </div>
          <div className="px-3 py-2 border-t border-slate-100 flex items-center justify-between">
            <span className="text-xs text-slate-500">{values.length} kişi seçildi</span>
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-xs font-semibold text-indigo-600 hover:text-indigo-700"
            >
              Temizle
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const AssignTaskModal: React.FC<AssignTaskModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  projects,
  users,
  initialWorkflowInstanceId,
}) => {
  const { t } = useTranslation(["common", "errors"]);
  const { user } = useAuth();
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    initialWorkflowInstanceId ?? null,
  );
  const [selectedActivityId, setSelectedActivityId] = useState<number | null>(
    null,
  );
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<number[]>([]);
  const [assignmentDescription, setAssignmentDescription] = useState("");
  const [activities, setActivities] = useState<ActivityDefinition[]>([]);
  const [projectMemberIds, setProjectMemberIds] = useState<number[]>([]);
  const [allowedActivityIds, setAllowedActivityIds] = useState<Set<number> | null>(null);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingAllowedActivities, setLoadingAllowedActivities] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedProjectId(initialWorkflowInstanceId ?? null);
    setSelectedActivityId(null);
    setSelectedAssigneeIds([]);
    setAssignmentDescription("");
    setProjectMemberIds([]);
  }, [isOpen, initialWorkflowInstanceId]);

  useEffect(() => {
    if (!isOpen) return;
    const loadActivities = async () => {
      try {
        setLoadingActivities(true);
        const data = await apiService.getActiveActivities();
        setActivities(Array.isArray(data) ? data : []);
      } catch (error) {
        toast.error(t("errors:loadFailed"));
      } finally {
        setLoadingActivities(false);
      }
    };
    void loadActivities();
  }, [isOpen, t]);

  useEffect(() => {
    if (!isOpen || !selectedProjectId) {
      setProjectMemberIds([]);
      setSelectedAssigneeIds([]);
      return;
    }

    const loadProjectMembers = async () => {
      try {
        setLoadingMembers(true);
        const data = await apiService.getWorkflowInstanceFormData(
          selectedProjectId,
        );
        const ids = Array.isArray(data?.projectMembers)
          ? data.projectMembers
              .map((value) => Number(value))
              .filter((value) => Number.isFinite(value))
          : [];
        setProjectMemberIds(ids);
        setSelectedAssigneeIds((prev) => prev.filter((id) => ids.includes(id)));
      } catch (error) {
        toast.error(t("errors:loadFailed"));
        setProjectMemberIds([]);
        setSelectedAssigneeIds([]);
      } finally {
        setLoadingMembers(false);
      }
    };

    void loadProjectMembers();
  }, [isOpen, selectedProjectId, t]);

  useEffect(() => {
    if (!isOpen || !selectedProjectId) {
      setAllowedActivityIds(null);
      return;
    }

    let cancelled = false;

    const loadAllowedActivities = async () => {
      try {
        setLoadingAllowedActivities(true);
        const selectedProject = projects.find(
          (project) => project.id === selectedProjectId,
        );
        const templateWorkflowId = selectedProject?.templateWorkflowId ?? null;

        const [userInstances, projectFormData, workflowTemplate] =
          await Promise.all([
            user?.id
              ? apiService.getUserWorkflowInstances(user.id).catch(() => [])
              : Promise.resolve([]),
            apiService.getWorkflowInstanceFormData(selectedProjectId).catch(
              () => null,
            ),
            templateWorkflowId
              ? apiService.getWorkflowById(templateWorkflowId).catch(() => null)
              : Promise.resolve(null),
          ]);

        if (cancelled) return;

        const instance = Array.isArray(userInstances)
          ? userInstances.find(
              (item: any) => Number(item?.id) === selectedProjectId,
            )
          : null;

        const snapshotActivityIds = parseNodesSnapshotActivityIds(
          instance?.nodesSnapshot,
        );
        const templateActivityIds: number[] = Array.isArray(
          (workflowTemplate as any)?.nodes,
        )
          ? (workflowTemplate as any).nodes
              .map((node: any) => Number(node?.data?.activityDefinitionId))
              .filter((id: number) => Number.isFinite(id))
          : [];

        const hasSelectionConfig = Boolean(
          instance?.activitySelectionConfigured ??
            projectFormData?.activitySelectionConfigured,
        );
        const adHocIds = (
          instance?.allowedAdHocActivityIds ??
          projectFormData?.allowedAdHocActivityIds ??
          []
        )
          .map((id: any) => Number(id))
          .filter((id: number) => Number.isFinite(id));

        const finalIds = new Set<number>();

        if (hasSelectionConfig) {
          if (snapshotActivityIds.length > 0) {
            snapshotActivityIds.forEach((id) => finalIds.add(id));
          } else {
            templateActivityIds.forEach((id: number) => finalIds.add(id));
          }
        } else {
          templateActivityIds.forEach((id: number) => finalIds.add(id));
        }

        adHocIds.forEach((id: number) => finalIds.add(id));
        setAllowedActivityIds(finalIds.size > 0 ? finalIds : null);
      } catch (error) {
        setAllowedActivityIds(null);
      } finally {
        if (!cancelled) {
          setLoadingAllowedActivities(false);
        }
      }
    };

    void loadAllowedActivities();

    return () => {
      cancelled = true;
    };
  }, [isOpen, projects, selectedProjectId, user?.id]);

  useEffect(() => {
    if (!selectedActivityId || !allowedActivityIds) return;
    if (!allowedActivityIds.has(selectedActivityId)) {
      setSelectedActivityId(null);
    }
  }, [allowedActivityIds, selectedActivityId]);

  useEffect(() => {
    if (!isOpen) return;
    if (!selectedActivityId) {
      setAssignmentDescription("");
      return;
    }

    const selectedActivity = activities.find(
      (activity) => activity.id === selectedActivityId,
    );
    setAssignmentDescription(selectedActivity?.description || "");
  }, [activities, isOpen, selectedActivityId]);

  const projectOptions = useMemo<SelectOption[]>(
    () =>
      projects
        .map((project) => ({
          value: project.id,
          label: project.projectCode
            ? `${project.projectCode} - ${project.projectName}`
            : project.projectName,
          subtitle: project.workflowName || undefined,
        }))
        .sort((a, b) => {
          if (a.value !== b.value) {
            return b.value - a.value;
          }
          return a.label.localeCompare(b.label, "tr-TR");
        }),
    [projects],
  );

  const activityOptions = useMemo<SelectOption[]>(
    () =>
      activities
        .filter((activity) =>
          allowedActivityIds ? allowedActivityIds.has(activity.id) : true,
        )
        .map((activity) => ({
          value: activity.id,
          label: activity.title || `Aktivite #${activity.id}`,
          subtitle: activity.description || undefined,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, "tr-TR")),
    [activities, allowedActivityIds],
  );

  const assigneeOptions = useMemo<SelectOption[]>(
    () =>
      users
        .filter((user) => projectMemberIds.includes(user.id))
        .filter((user) => user.role !== UserRole.ADMIN)
        .map((user) => {
          const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
          return {
            value: user.id,
            label: fullName || user.username || `Kullanıcı #${user.id}`,
            subtitle: user.username || user.email || undefined,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label, "tr-TR")),
    [projectMemberIds, users],
  );

  const handleSubmit = async () => {
    if (!selectedProjectId) {
      toast.error("Lütfen proje seçin");
      return;
    }
    if (!selectedActivityId) {
      toast.error("Lütfen aktivite seçin");
      return;
    }
    if (!selectedAssigneeIds.length) {
      toast.error("Lütfen en az bir çalışan seçin");
      return;
    }

    try {
      setSubmitting(true);
      await apiService.assignActivityToWorkflow(
        selectedProjectId,
        selectedActivityId,
        selectedAssigneeIds,
        assignmentDescription,
      );
      toast.success(
        selectedAssigneeIds.length > 1
          ? "Görev çalışanlara atandı"
          : "Görev çalışana atandı",
      );
      onSuccess();
      onClose();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t("errors:createFailed");
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-visible">
        <div className="px-6 py-4 bg-gradient-to-r from-indigo-600 to-blue-600 text-white flex items-center justify-between">
          <h3 className="text-lg font-semibold">Görev Ata</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md hover:bg-white/20 transition-colors"
            disabled={submitting}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {!initialWorkflowInstanceId && (
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                Proje
              </label>
              <SearchableSingleSelect
                value={selectedProjectId}
                onChange={setSelectedProjectId}
                options={projectOptions}
                placeholder="Proje seçin"
                searchPlaceholder="Proje ara..."
                emptyText="Proje bulunamadı"
              />
            </div>
          )}

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-slate-700">
              Aktivite
            </label>
            <SearchableSingleSelect
              value={selectedActivityId}
              onChange={setSelectedActivityId}
              options={activityOptions}
              placeholder={
                !selectedProjectId
                  ? "Önce proje seçin"
                  : loadingActivities || loadingAllowedActivities
                    ? "Yükleniyor..."
                    : "Aktivite seçin"
              }
              searchPlaceholder="Aktivite ara..."
              emptyText={
                selectedProjectId
                  ? "Projeye uygun aktivite bulunamadı"
                  : "Aktivite bulunamadı"
              }
              disabled={!selectedProjectId || loadingActivities || loadingAllowedActivities}
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <label className="block text-sm font-semibold text-slate-700">
              Açıklama
            </label>
            <textarea
              value={assignmentDescription}
              onChange={(e) => setAssignmentDescription(e.target.value)}
              rows={3}
              disabled={!selectedActivityId}
              placeholder="Aktivite açıklaması..."
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100 disabled:text-slate-400"
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <label className="block text-sm font-semibold text-slate-700">
              Çalışan Kişi
            </label>
            <SearchableMultiSelect
              values={selectedAssigneeIds}
              onChange={setSelectedAssigneeIds}
              options={assigneeOptions}
              placeholder={
                !selectedProjectId
                  ? "Önce proje seçin"
                  : loadingMembers
                    ? "Yükleniyor..."
                    : "Çalışan seçin"
              }
              searchPlaceholder="Çalışan ara..."
              emptyText="Çalışan bulunamadı"
              disabled={!selectedProjectId || loadingMembers}
            />
          </div>
        </div>

        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100"
            disabled={submitting}
          >
            {t("common:cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            disabled={submitting}
          >
            {submitting ? t("common:saving") : "Görev Ata"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignTaskModal;
